import { Icon } from '../../../../widgets/icon';
import { useDropdown } from '../../../../widgets/hooks/useDropdown';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { T, uploadFile } from 'src/shared/helpers';
import AngleUpIcon from 'src/shared/icons/angle-up-solid.svg';
import PlusIcon from 'src/shared/icons/plus-solid.svg';
import styled from 'styled-components';
import { aiPresetCategoriesQuery } from '../../queries/__generated__/aiPresetCategoriesQuery.graphql';
import { aiPresetsQuery } from '../../queries/__generated__/aiPresetsQuery.graphql';
import { AIPresetCategoriesQuery } from '../../queries/aiPresetCategories';
import { AIPresetsQuery } from '../../queries/aiPresets';
import { spin } from '../../styles/animations';
import { ToolType } from '../../types';
import { HiddenInput } from '../shared/sharedStyles';
import { PresetTypeConfig } from './presetTypeConfig';

/** Option type for the dropdown */
export interface PresetOption {
    value: string;
    label: string;
    thumbnailUrl?: string;
    isCustom?: boolean;
    isNone?: boolean;
    /** Scale factor for upscaler presets */
    factor?: number;
}

/** Group type for categorized presets */
export interface PresetOptionGroup {
    label: string;
    options: PresetOption[];
}

const DropdownButton = styled.button<{ $isOpen?: boolean; $hasSelection?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 4px;
    border: 1px solid ${(props) => (props.$isOpen ? '#8b5cf6' : props.theme.app.borderColor)};
    border-radius: 24px;
    background: ${(props) => (props.$isOpen ? 'rgba(139, 92, 246, 0.05)' : props.theme.app.backgroundColorBase)};
    color: ${(props) => props.theme.app.textColor};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    min-width: 100px;

    &:hover {
        border-color: #8b5cf6;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ButtonThumbnail = styled.div<{ $isNone?: boolean }>`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props) => (props.$isNone ? 'transparent' : '#f3f4f6')};
    border: ${(props) => (props.$isNone ? '1.5px dashed #d1d5db' : 'none')};
    position: relative;

    ${(props) =>
        props.$isNone &&
        `
		&::before {
			content: '';
			position: absolute;
			width: 50%;
			height: 1.5px;
			background: #9ca3af;
			transform: rotate(45deg);
		}
	`}

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const TextPreviewBadge = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    font-size: 10px;
    font-weight: 600;
    color: #374151;
`;

const ButtonLabel = styled.span`
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const SelectedName = styled.span`
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #6b7280;
    font-weight: 400;
`;

const ButtonArrow = styled.div<{ $isOpen?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
    transform: ${(props) => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};

    ${Icon} {
        width: 12px;
        height: 12px;
        fill: #6b7280;
    }
`;

const DropdownMenu = styled.div`
    overflow-y: auto;
    max-height: 500px;
    width: 408px;
    padding: 12px;
`;

const MenuSection = styled.div`
    &:not(:last-child) {
        padding-bottom: 8px;
        margin-bottom: 8px;
    }
`;

const MenuSectionLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: #6b7280;
    padding: 0 4px 12px;
    letter-spacing: 0.5px;
`;

const MenuGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
`;

const SpecialOptionsGrid = styled.div`
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(3, 1fr);
`;

const GridMenuItem = styled.button<{ $selected?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 8px;
    border: 2px solid ${(props) => (props.$selected ? '#8b5cf6' : 'transparent')};
    border-radius: 12px;
    background: ${(props) => (props.$selected ? 'rgba(139, 92, 246, 0.05)' : 'transparent')};
    color: ${(props) => (props.$selected ? '#7c3aed' : '#1f2937')};
    font-size: 11px;
    font-weight: ${(props) => (props.$selected ? 500 : 400)};
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: center;

    &:hover {
        background: ${(props) => (props.$selected ? 'rgba(139, 92, 246, 0.1)' : '#f3f4f6')};
        border-color: ${(props) => (props.$selected ? '#8b5cf6' : '#e5e7eb')};
    }
`;

const GridItemThumbnail = styled.div<{ $isCustom?: boolean; $isNone?: boolean }>`
    width: 100%;
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(props) => (props.$isCustom || props.$isNone ? 'transparent' : '#f3f4f6')};
    border: ${(props) => (props.$isCustom ? '2px dashed #d1d5db' : props.$isNone ? '2px dashed #d1d5db' : '1px solid #e5e7eb')};
    position: relative;

    ${(props) =>
        props.$isNone &&
        `
		&::before {
			content: '';
			position: absolute;
			width: 40%;
			height: 2px;
			background: #9ca3af;
			transform: rotate(45deg);
		}

		&::after {
			content: '';
			position: absolute;
			width: 24px;
			height: 24px;
			border: 2px solid #d1d5db;
			border-radius: 50%;
		}
	`}

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    ${Icon} {
        width: 20px;
        height: 20px;
        fill: #9ca3af;
    }
`;

const GridItemTextPreview = styled.div`
    width: 100%;
    aspect-ratio: 1;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    font-size: 16px;
    font-weight: 600;
    color: #374151;
`;

const GridItemLabel = styled.span`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
    height: 2.6em;
    width: 100%;
    word-break: break-word;
`;

const UploadSpinner = styled.div`
    width: 18px;
    height: 18px;
    border: 2px solid #e5e7eb;
    border-top-color: #8b5cf6;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
`;

export interface PresetDropdownValue {
    id: string;
    thumbnailUrl?: string;
    factor?: number;
}

interface PresetDropdownProps {
    toolId: ToolType;
    typeConfig: PresetTypeConfig;
    selectedValue: PresetDropdownValue | null;
    onSelect: (value: PresetDropdownValue | null) => void;
    /** Whether the dropdown is disabled */
    disabled?: boolean;
    /** Tooltip to show when the dropdown is disabled */
    disabledTooltip?: string;
}

/** Internal component that fetches data and renders the dropdown */
const PresetDropdownContent: FC<PresetDropdownProps> = ({ toolId, typeConfig, selectedValue, onSelect, disabled, disabledTooltip }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState<{ id: string; url: string; name: string } | null>(null);
    const [setOpen, setClose, isOpen, Dropdown] = useDropdown();

    // Fetch presets
    const categoryToolId = typeConfig.categoryToolId || toolId;
    const presetsData = useLazyLoadQuery<aiPresetsQuery>(AIPresetsQuery, {
        toolId: categoryToolId,
        presetType: typeConfig.presetType || undefined
    });

    // Fetch categories if this type has categories
    const categoriesData = useLazyLoadQuery<aiPresetCategoriesQuery>(
        AIPresetCategoriesQuery,
        {
            toolId: categoryToolId,
            presetType: typeConfig.presetType
        },
        { fetchPolicy: typeConfig.hasCategories ? 'store-or-network' : 'store-only' }
    );

    const allPresets = presetsData.aiPresets ?? [];
    const categories = categoriesData.aiPresetCategories ?? [];

    // Build options based on whether we have categories
    const { specialOptions, groupedOptions, flatOptions } = useMemo(() => {
        const special: PresetOption[] = [];
        const grouped: PresetOptionGroup[] = [];
        const flat: PresetOption[] = [];

        // Add "Custom" option if upload is allowed
        if (typeConfig.allowUpload !== false) {
            if (uploadedImage) {
                special.push({
                    value: uploadedImage.id,
                    label: uploadedImage.name,
                    thumbnailUrl: uploadedImage.url,
                    isCustom: false
                });
            }
            special.push({
                value: 'custom',
                label: T._('Custom', 'Admin'),
                isCustom: true
            });
        }

        // Add "None" option if allowed
        if (typeConfig.allowNone !== false && !typeConfig.mandatory) {
            special.push({
                value: 'none',
                label: T._('None', 'Admin'),
                isNone: true
            });
        }

        // If we have categories, group presets by category
        if (typeConfig.hasCategories && categories.length > 0) {
            type PresetType = (typeof allPresets)[number];
            const presetsByCategory = new Map<string, PresetType[]>();

            for (const preset of allPresets) {
                const catId = preset.categoryId || 'uncategorized';
                if (!presetsByCategory.has(catId)) {
                    presetsByCategory.set(catId, []);
                }
                presetsByCategory.get(catId)!.push(preset);
            }

            for (const category of categories) {
                const categoryPresets = presetsByCategory.get(category.id) || [];
                if (categoryPresets.length > 0) {
                    grouped.push({
                        label: T._(category.name, 'Admin'),
                        options: categoryPresets.map((p) => ({
                            value: p.id,
                            label: p.name,
                            thumbnailUrl: p.previewUrl || p.imageUrl || undefined,
                            factor: p.factor ?? undefined
                        }))
                    });
                }
            }

            // Add uncategorized presets if any
            const uncategorized = presetsByCategory.get('uncategorized');
            if (uncategorized && uncategorized.length > 0) {
                grouped.push({
                    label: T._('Other', 'Admin'),
                    options: uncategorized.map((p) => ({
                        value: p.id,
                        label: p.name,
                        thumbnailUrl: p.previewUrl || p.imageUrl || undefined,
                        factor: p.factor ?? undefined
                    }))
                });
            }
        } else {
            // No categories, add all presets as flat options
            for (const preset of allPresets) {
                flat.push({
                    value: preset.id,
                    label: preset.name,
                    thumbnailUrl: preset.previewUrl || preset.imageUrl || undefined,
                    factor: preset.factor ?? undefined
                });
            }
        }

        return { specialOptions: special, groupedOptions: grouped, flatOptions: flat };
    }, [typeConfig, allPresets, categories, uploadedImage]);

    // Find selected option for display
    const selectedOption = useMemo((): PresetOption | null => {
        if (!selectedValue) return null;

        // Check if it's the uploaded image
        if (uploadedImage && selectedValue.id === uploadedImage.id) {
            return {
                value: uploadedImage.id,
                label: uploadedImage.name,
                thumbnailUrl: uploadedImage.url,
                isCustom: false
            };
        }

        // Search in special options
        for (const opt of specialOptions) {
            if (opt.value === selectedValue.id) return opt;
        }

        // Search in flat options
        for (const opt of flatOptions) {
            if (opt.value === selectedValue.id) return opt;
        }

        // Search in grouped options
        for (const group of groupedOptions) {
            for (const opt of group.options) {
                if (opt.value === selectedValue.id) return opt;
            }
        }

        return null;
    }, [selectedValue, specialOptions, flatOptions, groupedOptions, uploadedImage]);

    // Handle button click
    const handleButtonClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (isOpen) {
                setClose();
            } else {
                setOpen(e.currentTarget as HTMLElement, 'top', 'left');
            }
        },
        [isOpen, setOpen, setClose]
    );

    // Handle option select
    const handleOptionSelect = useCallback(
        (option: PresetOption) => {
            if (option.value === 'custom') {
                // Trigger file upload
                fileInputRef.current?.click();
                setClose();
                return;
            }

            if (option.value === 'none' || option.isNone) {
                onSelect(null);
                setClose();
                return;
            }

            onSelect({
                id: option.value,
                thumbnailUrl: option.thumbnailUrl,
                factor: option.factor
            });
            setClose();
        },
        [onSelect, setClose]
    );

    // Handle file upload
    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            setIsUploading(true);
            try {
                const url = await uploadFile(file);
                const id = `custom-${Date.now()}`;
                const uploaded = { id, url, name: file.name };
                setUploadedImage(uploaded);
                onSelect({
                    id,
                    thumbnailUrl: url
                });
            } catch (error) {
                console.error('Upload failed:', error);
            } finally {
                setIsUploading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        },
        [onSelect]
    );

    // Render the thumbnail for selected value
    const renderButtonThumbnail = () => {
        if (!selectedOption) {
            return <ButtonThumbnail $isNone />;
        }

        if (selectedOption.isNone) {
            return <ButtonThumbnail $isNone />;
        }

        if (selectedOption.thumbnailUrl) {
            return (
                <ButtonThumbnail>
                    <img src={selectedOption.thumbnailUrl} alt={selectedOption.label} />
                </ButtonThumbnail>
            );
        }

        if (selectedOption.factor !== undefined) {
            return <TextPreviewBadge>{selectedOption.label}</TextPreviewBadge>;
        }

        return <ButtonThumbnail />;
    };

    // Render grid item thumbnail (for grid layout)
    const renderGridItemThumbnail = (option: PresetOption) => {
        if (option.isCustom) {
            return (
                <GridItemThumbnail $isCustom>
                    {isUploading ? (
                        <UploadSpinner />
                    ) : (
                        <Icon>
                            <PlusIcon />
                        </Icon>
                    )}
                </GridItemThumbnail>
            );
        }

        if (option.isNone) {
            return <GridItemThumbnail $isNone />;
        }

        if (option.thumbnailUrl) {
            return (
                <GridItemThumbnail>
                    <img src={option.thumbnailUrl} alt={option.label} />
                </GridItemThumbnail>
            );
        }

        if (option.factor !== undefined) {
            return <GridItemTextPreview>{option.label}</GridItemTextPreview>;
        }

        return <GridItemThumbnail />;
    };

    const displayLabel = T._(typeConfig.label, 'Admin');

    const isDisabled = disabled || isUploading;

    const selectedNameFull = selectedOption && !selectedOption.isNone ? selectedOption.label : null;
    const selectedName = selectedNameFull && selectedNameFull.length > 10 ? selectedNameFull.slice(0, 20) + '...' : selectedNameFull;

    const dropdownButton = (
        <DropdownButton onClick={handleButtonClick} disabled={isDisabled} $isOpen={isOpen} $hasSelection={!!selectedOption}>
            {renderButtonThumbnail()}
            <ButtonLabel>{displayLabel}</ButtonLabel>
            {selectedName && <SelectedName>{selectedName}</SelectedName>}
            <ButtonArrow $isOpen={isOpen}>
                <Icon>
                    <AngleUpIcon />
                </Icon>
            </ButtonArrow>
        </DropdownButton>
    );

    return (
        <>
            {isDisabled && disabledTooltip ? (
                <ZTooltip position='top' title={disabledTooltip}>
                    <div>{dropdownButton}</div>
                </ZTooltip>
            ) : (
                dropdownButton
            )}

            {isOpen && (
                <Dropdown>
                    <DropdownMenu>
                        {/* Special options (Custom, None) */}
                        {specialOptions.length > 0 && (
                            <MenuSection>
                                <SpecialOptionsGrid>
                                    {specialOptions.map((option) => (
                                        <GridMenuItem
                                            key={option.value}
                                            $selected={selectedValue?.id === option.value}
                                            onClick={() => handleOptionSelect(option)}
                                        >
                                            {renderGridItemThumbnail(option)}
                                            <GridItemLabel>{option.label}</GridItemLabel>
                                        </GridMenuItem>
                                    ))}
                                </SpecialOptionsGrid>
                            </MenuSection>
                        )}

                        {/* Flat options (no categories) */}
                        {flatOptions.length > 0 && (
                            <MenuSection>
                                <MenuGrid>
                                    {flatOptions.map((option) => (
                                        <GridMenuItem
                                            key={option.value}
                                            $selected={selectedValue?.id === option.value}
                                            onClick={() => handleOptionSelect(option)}
                                        >
                                            {renderGridItemThumbnail(option)}
                                            {!typeConfig.hideLabels && <GridItemLabel>{option.label}</GridItemLabel>}
                                        </GridMenuItem>
                                    ))}
                                </MenuGrid>
                            </MenuSection>
                        )}

                        {/* Grouped options (with categories) */}
                        {groupedOptions.map((group) => (
                            <MenuSection key={group.label}>
                                <MenuSectionLabel>{group.label}</MenuSectionLabel>
                                <MenuGrid>
                                    {group.options.map((option) => (
                                        <GridMenuItem
                                            key={option.value}
                                            $selected={selectedValue?.id === option.value}
                                            onClick={() => handleOptionSelect(option)}
                                        >
                                            {renderGridItemThumbnail(option)}
                                            {!typeConfig.hideLabels && <GridItemLabel>{option.label}</GridItemLabel>}
                                        </GridMenuItem>
                                    ))}
                                </MenuGrid>
                            </MenuSection>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            )}

            {typeConfig.allowUpload !== false && <HiddenInput ref={fileInputRef} type='file' accept='image/*' onChange={handleFileChange} />}
        </>
    );
};

const DropdownWrapper = styled.div`
    display: inline-block;
`;

const LoadingButton = styled(DropdownButton)`
    opacity: 0.6;
    cursor: default;
`;

/** Preset dropdown component with Suspense boundary */
export const PresetDropdown: FC<PresetDropdownProps> = (props) => {
    return (
        <DropdownWrapper>
            <React.Suspense
                fallback={
                    <LoadingButton disabled>
                        <ButtonThumbnail $isNone />
                        <ButtonLabel>{T._(props.typeConfig.label, 'Admin')}</ButtonLabel>
                        <ButtonArrow>
                            <Icon>
                                <AngleUpIcon />
                            </Icon>
                        </ButtonArrow>
                    </LoadingButton>
                }
            >
                <PresetDropdownContent {...props} />
            </React.Suspense>
        </DropdownWrapper>
    );
};

export default PresetDropdown;
